import { useMemo } from 'react';
import { useAppointmentTypes } from '@/hooks/use-appointment-types';
import { useLocations } from '@/hooks/use-locations';
import { useProviders } from '@/hooks/use-providers';

/**
 * Display names for the ids an audience filter holds.
 *
 * The spec stores uuids, which is right — a campaign must not break when a
 * location is renamed — but a description that reads "at 4f2a…" is not English.
 * The describer takes this map and degrades a criterion it cannot fully resolve
 * to a count ("at 2 locations") rather than half-naming an audience.
 *
 * Labels match the pickers the author chose the values with, deliberately: a
 * provider shown as "Anita Sahai" in the filter and "Dr Sahai" in the sentence
 * reads as two different people.
 */
export interface FilterNames {
  names: Readonly<Record<string, string>>;
  /**
   * Every dictionary has arrived.
   *
   * The describer degrades a criterion it cannot fully resolve to a count — "at
   * 2 locations" — which is honest to read but must never be the text handed to
   * the compiler: the model cannot resolve that phrase back to two ids, so the
   * criterion is dropped and the audience silently widens to every location.
   * The editor gates the text editor on this rather than on time passing.
   */
  ready: boolean;
}

export function useFilterNames(): FilterNames {
  const locationsQuery = useLocations();
  const providersQuery = useProviders();
  const appointmentTypesQuery = useAppointmentTypes();
  const { data: locations } = locationsQuery;
  const { data: providers } = providersQuery;
  const { data: appointmentTypes } = appointmentTypesQuery;

  const names = useMemo(() => {
    const names: Record<string, string> = {};
    for (const location of locations?.items ?? []) names[location.id] = location.name;
    for (const provider of providers?.items ?? []) {
      names[provider.id] = `${provider.firstName} ${provider.lastName}`;
    }
    for (const type of appointmentTypes ?? []) names[type.id] = type.name;
    return names;
  }, [locations, providers, appointmentTypes]);

  // Success, not merely "settled": a failed lookup leaves the map incomplete,
  // and an incomplete map is exactly the case that loses a filter.
  return {
    names,
    ready: locationsQuery.isSuccess && providersQuery.isSuccess && appointmentTypesQuery.isSuccess,
  };
}
